import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import type {
  BookingAction,
  BookingSourceType,
  HoldAction,
  HoldReleaseReasonType,
  ParticipantType,
  ScheduleConflictReason,
} from "../types";
import { formatLabel, toIsoUtc } from "../labels";

const BOOKING_ACTIONS: BookingAction[] = [
  "request_confirmation",
  "request_payment",
  "confirm",
  "start",
  "complete",
  "cancel",
  "fail",
  "dispute",
  "refund_partial",
  "refund_full",
];

const HOLD_ACTIONS: HoldAction[] = [
  "release",
  "cancel",
  "supersede",
  "mark_expiring",
  "expire",
];

const RELEASE_REASONS: HoldReleaseReasonType[] = [
  "payment_success",
  "payment_failed",
  "customer_cancelled",
  "expiry",
  "maintenance",
  "blackout",
  "hq_remediation",
  "superseded_by_retry",
  "policy_restriction",
];

const SOURCES: BookingSourceType[] = [
  "direct",
  "community",
  "creator_campaign",
  "business",
  "marketplace_opportunity",
  "future_governed_type",
];

const PARTICIPANT_TYPES: ParticipantType[] = [
  "participant",
  "attendee",
  "invitee",
  "beneficiary",
];

export function CreateBookingDialog({
  open,
  defaultBusinessId,
  defaultResourceId,
  onClose,
  onSubmit,
  onCheckConflicts,
  busy = false,
  error = null,
  conflictResult = null,
}: {
  open: boolean;
  defaultBusinessId?: string;
  defaultResourceId?: string;
  onClose: () => void;
  onSubmit: (input: {
    customer_user_id: string;
    business_id: string;
    starts_at: string;
    ends_at: string;
    currency: string;
    business_capability_id?: string | null;
    bookable_resource_id?: string | null;
    staff_user_id?: string | null;
    booking_source_type?: BookingSourceType | null;
    booking_source_id?: string | null;
    allocation_hold_id?: string | null;
    skip_hold?: boolean;
  }) => Promise<void>;
  onCheckConflicts: (input: {
    business_id: string;
    starts_at: string;
    ends_at: string;
    bookable_resource_id?: string | null;
    business_capability_id?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
  conflictResult?: { clear: boolean; conflicts: ScheduleConflictReason[] } | null;
}) {
  const [customerUserId, setCustomerUserId] = useState("");
  const [businessId, setBusinessId] = useState(defaultBusinessId ?? "");
  const [resourceId, setResourceId] = useState(defaultResourceId ?? "");
  const [capabilityId, setCapabilityId] = useState("");
  const [staffUserId, setStaffUserId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [sourceType, setSourceType] = useState<BookingSourceType | "">("direct");
  const [sourceId, setSourceId] = useState("");
  const [holdId, setHoldId] = useState("");
  const [skipHold, setSkipHold] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (defaultBusinessId) setBusinessId(defaultBusinessId);
    if (defaultResourceId) setResourceId(defaultResourceId);
  }, [open, defaultBusinessId, defaultResourceId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Booking"
      actions={
        <>
          <Button
            variant="secondary"
            disabled={busy || !businessId.trim() || !startsAt || !endsAt}
            onClick={() =>
              void onCheckConflicts({
                business_id: businessId.trim(),
                starts_at: toIsoUtc(startsAt),
                ends_at: toIsoUtc(endsAt),
                ...(resourceId.trim()
                  ? { bookable_resource_id: resourceId.trim() }
                  : {}),
                ...(capabilityId.trim()
                  ? { business_capability_id: capabilityId.trim() }
                  : {}),
              })
            }
          >
            Check conflicts
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              !customerUserId.trim() ||
              !businessId.trim() ||
              !startsAt ||
              !endsAt ||
              !currency.trim()
            }
            onClick={() =>
              void onSubmit({
                customer_user_id: customerUserId.trim(),
                business_id: businessId.trim(),
                starts_at: toIsoUtc(startsAt),
                ends_at: toIsoUtc(endsAt),
                currency: currency.trim(),
                ...(resourceId.trim()
                  ? { bookable_resource_id: resourceId.trim() }
                  : {}),
                ...(capabilityId.trim()
                  ? { business_capability_id: capabilityId.trim() }
                  : {}),
                ...(staffUserId.trim()
                  ? { staff_user_id: staffUserId.trim() }
                  : {}),
                ...(sourceType && sourceId.trim()
                  ? {
                      booking_source_type: sourceType,
                      booking_source_id: sourceId.trim(),
                    }
                  : {}),
                ...(holdId.trim() ? { allocation_hold_id: holdId.trim() } : {}),
                ...(skipHold ? { skip_hold: true } : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          A Hold is a separate inventory reservation, not a Booking status.
          Creating a Booking may attach an existing Hold or skip one when HQ
          remediates.
        </Text>
        <Input
          label="Customer User ID"
          value={customerUserId}
          onChange={(event) => setCustomerUserId(event.target.value)}
        />
        <Input
          label="Business ID"
          value={businessId}
          onChange={(event) => setBusinessId(event.target.value)}
        />
        <Input
          label="Bookable Resource ID (optional)"
          value={resourceId}
          onChange={(event) => setResourceId(event.target.value)}
        />
        <Input
          label="Business Capability ID (optional)"
          value={capabilityId}
          onChange={(event) => setCapabilityId(event.target.value)}
        />
        <Input
          label="Staff User ID (optional)"
          value={staffUserId}
          onChange={(event) => setStaffUserId(event.target.value)}
        />
        <Input
          label="Starts"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <Input
          label="Ends"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        <Input
          label="Currency"
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
        />
        <Select
          label="Source type"
          value={sourceType}
          onChange={(event) =>
            setSourceType(event.target.value as BookingSourceType | "")
          }
          options={[
            { value: "", label: "None" },
            ...SOURCES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Input
          label="Source ID (when source type is set)"
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
        />
        <Input
          label="Allocation Hold ID (optional)"
          value={holdId}
          onChange={(event) => setHoldId(event.target.value)}
        />
        <Checkbox
          label="Skip Hold (HQ remediation)"
          checked={skipHold}
          onChange={(event) => setSkipHold(event.target.checked)}
        />
        {conflictResult ? (
          <Alert
            tone={conflictResult.clear ? "success" : "warning"}
            title={
              conflictResult.clear
                ? "Schedule is clear"
                : "Conflicts found"
            }
          >
            {conflictResult.clear
              ? "No overlapping bookings, holds, blackouts, or maintenance for this window."
              : conflictResult.conflicts.map(formatLabel).join(", ")}
          </Alert>
        ) : null}
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function BookingActionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { action: BookingAction; reason?: string }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<BookingAction>("confirm");
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Booking status action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "cancel" ||
              action === "fail" ||
              action === "dispute" ||
              action === "refund_full"
                ? "danger"
                : "primary"
            }
            loading={busy}
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Refunds record Booking status only. Money movement stays in Finance.
        </Text>
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as BookingAction)}
          options={BOOKING_ACTIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Reason (optional, required by some transitions)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function EditBookingDialog({
  open,
  startsAt,
  endsAt,
  staffUserId,
  currency,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  startsAt: string;
  endsAt: string;
  staffUserId: string;
  currency: string;
  onClose: () => void;
  onSubmit: (input: {
    starts_at: string;
    ends_at: string;
    staff_user_id: string | null;
    currency: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [start, setStart] = useState(startsAt);
  const [end, setEnd] = useState(endsAt);
  const [staff, setStaff] = useState(staffUserId);
  const [ccy, setCcy] = useState(currency);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Booking window"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                starts_at: toIsoUtc(start) || start,
                ends_at: toIsoUtc(end) || end,
                staff_user_id: staff.trim() || null,
                currency: ccy.trim(),
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="Starts (ISO or local)"
          value={start}
          onChange={(event) => setStart(event.target.value)}
        />
        <Input
          label="Ends (ISO or local)"
          value={end}
          onChange={(event) => setEnd(event.target.value)}
        />
        <Input
          label="Staff User ID"
          value={staff}
          onChange={(event) => setStaff(event.target.value)}
        />
        <Input
          label="Currency"
          value={ccy}
          onChange={(event) => setCcy(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Update failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function AddParticipantDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    user_id: string;
    participant_type: ParticipantType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [userId, setUserId] = useState("");
  const [participantType, setParticipantType] =
    useState<ParticipantType>("participant");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add participant"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!userId.trim()}
            onClick={() =>
              void onSubmit({
                user_id: userId.trim(),
                participant_type: participantType,
              })
            }
          >
            Add
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Select
          label="Participant type"
          value={participantType}
          onChange={(event) =>
            setParticipantType(event.target.value as ParticipantType)
          }
          options={PARTICIPANT_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Add failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function ReasonActionDialog({
  open,
  title,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => void onSubmit(reason.trim())}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function HoldActionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: HoldAction;
    reason?: string;
    release_reason_type?: HoldReleaseReasonType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<HoldAction>("release");
  const [reason, setReason] = useState("");
  const [releaseReason, setReleaseReason] =
    useState<HoldReleaseReasonType>("hq_remediation");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Hold action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(action === "release"
                  ? { release_reason_type: releaseReason }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Holds stay independent from Booking status. Convert is a separate
          action that requires acknowledging payment success.
        </Text>
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as HoldAction)}
          options={HOLD_ACTIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {action === "release" ? (
          <Select
            label="Release reason"
            value={releaseReason}
            onChange={(event) =>
              setReleaseReason(event.target.value as HoldReleaseReasonType)
            }
            options={RELEASE_REASONS.map((item) => ({
              value: item,
              label: formatLabel(item),
            }))}
          />
        ) : null}
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function ConvertHoldDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    payment_success_acknowledged: true;
    reason?: string;
    booking_status?: "initiated" | "confirmed";
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [bookingStatus, setBookingStatus] = useState<"initiated" | "confirmed">(
    "confirmed",
  );
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Convert Hold to Booking"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!acknowledged}
            onClick={() =>
              void onSubmit({
                payment_success_acknowledged: true,
                booking_status: bookingStatus,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Convert
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Conversion acknowledges payment success. HQ does not move money here.
        </Text>
        <Select
          label="Resulting Booking status"
          value={bookingStatus}
          onChange={(event) =>
            setBookingStatus(event.target.value as "initiated" | "confirmed")
          }
          options={[
            { value: "confirmed", label: "Confirmed" },
            { value: "initiated", label: "Initiated" },
          ]}
        />
        <Checkbox
          label="I acknowledge payment success"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Convert failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
